import React, { useEffect, useState } from 'react';
import { auditLogsService } from '@/services/auditLogs';
import { getApiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

function formatWhen(ts) {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return String(ts);
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return String(ts);
  }
}

function typeBadgeVariant(type) {
  if (type === 'Create') return 'default';
  if (type === 'Update') return 'secondary';
  if (type === 'Delete') return 'destructive';
  return 'outline';
}

/**
 * Reusable audit trail for a single entity (appointment, consultation, etc.).
 * @param {string} entityType - backend entityType, e.g. 'appointment', 'consultation'
 * @param {string} [entityId] - Mongo id; if missing, shows placeholder
 * @param {string} [title] - card title
 * @param {string} [description] - card description
 * @param {boolean} [compact] - tighter padding, no outer Card when true (embed in dialog)
 * @param {number} [refreshTrigger] - increment to refetch after mutations
 */
export function AuditLogList({
  entityType,
  entityId,
  title = 'Activity log',
  description = 'Who created or changed this record',
  compact = false,
  refreshTrigger = 0,
  className,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!entityType || !entityId) {
      setRows([]);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    (async () => {
      try {
        const { data } = await auditLogsService.getByEntity(entityType, entityId);
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          toast.error(getApiErrorMessage(e));
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entityType, entityId, refreshTrigger]);

  const inner = (
    <>
      {!entityId ? (
        <p className="text-sm text-muted-foreground py-2">
          Save this record first to see activity.
        </p>
      ) : loading ? (
        <div className="space-y-2 py-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">No activity recorded yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">When</TableHead>
              <TableHead className="w-[90px]">Action</TableHead>
              <TableHead>Summary</TableHead>
              <TableHead className="w-[140px]">User</TableHead>
              <TableHead className="min-w-[200px]">Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="text-muted-foreground whitespace-nowrap align-top">
                  {formatWhen(row.timestamp)}
                </TableCell>
                <TableCell className="align-top">
                  <Badge variant={typeBadgeVariant(row.type)}>{row.type}</Badge>
                </TableCell>
                <TableCell className="align-top font-medium">{row.action}</TableCell>
                <TableCell className="align-top text-sm">{row.user}</TableCell>
                <TableCell className="text-muted-foreground text-sm align-top">
                  {row.details || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );

  if (compact) {
    return (
      <div className={cn('space-y-3', className)}>
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          {description ? (
            <p className="text-xs text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {inner}
      </div>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">{inner}</CardContent>
    </Card>
  );
}
